import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../../store';
import { setToken } from '../../store/authSlice';
import apiClient from '../../api/client';

declare global {
  interface Window {
    VKIDSDK?: any;
  }
}

const VK_APP_ID = parseInt(process.env.REACT_APP_VK_APP_ID || '0', 10);

export const VKIDWidget: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();

  useEffect(() => {
    if (!VK_APP_ID || !containerRef.current) return;

    const initWidget = () => {
      if (!window.VKIDSDK || !containerRef.current) return;
      const VKID = window.VKIDSDK;

      VKID.Config.init({
        app: VK_APP_ID,
        // redirectUrl must match a domain whitelisted in the VK app settings.
        // Using window.location.origin makes this work across domains automatically.
        redirectUrl: `${window.location.origin}/api/auth/oauth/vk/callback`,
        responseMode: VKID.ConfigResponseMode.Callback,
        source: VKID.ConfigSource.LOWCODE,
        scope: '',
      });

      const oAuth = new VKID.OAuthList();
      oAuth
        .render({
          container: containerRef.current,
          scheme: 'dark',
          oauthList: ['vkid', 'ok_ru', 'mail_ru'],
        })
        .on(VKID.WidgetEvents.ERROR, (err: unknown) => {
          console.error('VK ID widget error', err);
        })
        .on(VKID.OAuthListInternalEvents.LOGIN_SUCCESS, async (payload: { code: string; device_id: string }) => {
          try {
            const data = await VKID.Auth.exchangeCode(payload.code, payload.device_id);
            const res = await apiClient.post<{ token: string }>('/auth/vkid/callback', {
              access_token: data.access_token,
            });
            dispatch(setToken(res.data.token));
            navigate('/dashboard', { replace: true });
          } catch (err) {
            console.error('VK ID auth failed', err);
            navigate('/login?error=vkid_failed', { replace: true });
          }
        });
    };

    const SCRIPT_ID = 'vkid-sdk';
    let script = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null;

    if (!script) {
      script = document.createElement('script');
      script.id = SCRIPT_ID;
      script.src = 'https://unpkg.com/@vkid/sdk@<3.0.0/dist-sdk/umd/index.js';
      script.onload = initWidget;
      document.head.appendChild(script);
    } else if (window.VKIDSDK) {
      initWidget();
    } else {
      script.addEventListener('load', initWidget);
    }

    return () => {
      if (containerRef.current) {
        containerRef.current.innerHTML = '';
      }
    };
  }, [dispatch, navigate]);

  if (!VK_APP_ID) return null;

  return <div ref={containerRef} className="vkid-widget" />;
};
